import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { Block } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { LocationPicker } from "components/_dashboard/listingsMatch/LocationPicker";
import useLocationGroups from "hooks/useLocationGroups";
import { locationIdentityKey, validateLocationGroup } from "utils/locations/locationGroups";
import {
  MAX_LOCATIONS_PER_LOCATION_GROUP,
  MAX_LOCATION_GROUP_NAME_LENGTH
} from "../../../@types/sharedSchema";
import type { LocationGroup, ProcessedListingAddress } from "../../../@types/sharedSchema";

/**
 * Create or edit one group: a name, an optional badge, and the areas.
 *
 * ── The picker below is an AREAS picker, and the two flags say so ─────────────
 *
 * `includeEmpty` is deliberately left at its default `true`: a group is a saved
 * SET of areas, and an area holding no listings today is a perfectly good member
 * — a listing there tomorrow is exactly what the realtor is waiting for. Only
 * listing-SEARCH pickers pass `false`, where an empty place is a dead option.
 *
 * `hideQuickTabs` is the one call site that genuinely needs it: without it the
 * editor's own picker would offer the realtor their groups to put inside a group.
 *
 * ── Errors are routed to the field that caused them ──────────────────────────
 *
 * `saveGroup` throws an i18n KEY rather than a message, and the key names the
 * rule that failed — so the same string that renders the message also decides
 * which control it renders under. That keeps one list of rules
 * (`validateLocationGroup`) instead of one here and one in the hook.
 */

/**
 * Ten badges, no dependency. An emoji picker would be a package and a modal for
 * a decoration; these are the shapes a realtor actually reaches for when naming a
 * patch of a city, and the eleventh option is "none".
 */
const EMOJI_CHOICES = ["📍", "🏙️", "🏡", "🌊", "🌳", "🏢", "🗺️", "⭐", "🔥", "🧭"];

/** Which control an i18n error key belongs under. */
const NAME_ERROR_KEYS = new Set([
  "locationGroups.errors.nameEmpty",
  "locationGroups.errors.nameTooLong"
]);
const AREAS_ERROR_KEYS = new Set([
  "locationGroups.errors.noLocations",
  "locationGroups.errors.tooManyLocations"
]);

type LocationGroupEditorProps = {
  /** Absent = create. */
  group?: LocationGroup;
  onCancel: () => void;
  onSaved: (group: LocationGroup) => void;
  /**
   * Whether the form holds edits that a close would throw away, so the host can
   * ask before discarding them — the same guard the contact dialog puts on its
   * own backdrop. Reported from here because only this component knows what the
   * group looked like when the editor opened.
   */
  onDirtyChange?: (dirty: boolean) => void;
};

export function LocationGroupEditor({
  group,
  onCancel,
  onSaved,
  onDirtyChange
}: LocationGroupEditorProps): JSX.Element {
  const { t } = useTranslation();
  const { saveGroup } = useLocationGroups();

  const [name, setName] = useState<string>(group?.name ?? "");
  const [emoji, setEmoji] = useState<string>(group?.emoji ?? "");
  // Seeded through `useState`'s initializer so the array identity is stable:
  // `LocationPicker` mirrors `pickedLocations` into its own state on every change
  // of that prop, and a fresh array per render would re-seed it forever.
  const [locations, setLocations] = useState<ProcessedListingAddress[]>(
    () => group?.locations ?? []
  );

  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  /**
   * Save is disabled while the form is invalid, so a field can only ever be
   * TOLD it is wrong by the realtor themselves leaving it that way — there is no
   * failed-submit moment to hang an error on, and complaining about a form that
   * has not been filled in yet is noise. Blurring an empty name is one such
   * moment; clearing every area you had picked is the other.
   */
  const [nameBlurred, setNameBlurred] = useState(false);
  const [areasEmptied, setAreasEmptied] = useState(false);

  const trimmedName = name.trim();
  const validation = validateLocationGroup({ name: trimmedName, locations });
  const isValid = !("reason" in validation);

  /**
   * Both sides serialized the same way, so an untouched editor compares equal.
   * A CREATE is dirty as soon as anything has been typed or picked; an EDIT only
   * once it differs from the group it opened on.
   *
   * Locations are compared on their identity keys rather than whole objects: a
   * picked area carries the group stamp and a stored one does not, so the raw
   * objects differ even when the selection is untouched.
   */
  const isDirty =
    trimmedName !== (group?.name ?? "") ||
    emoji !== (group?.emoji ?? "") ||
    JSON.stringify(locations.map(locationIdentityKey)) !==
      JSON.stringify((group?.locations ?? []).map(locationIdentityKey));

  useEffect(() => {
    onDirtyChange?.(isDirty);
    // A saved or cancelled editor unmounts without ever reporting its way back
    // down, and the host would keep guarding a form that is gone.
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const localNameErrorKey =
    "reason" in validation && validation.reason === "name-empty"
      ? "locationGroups.errors.nameEmpty"
      : "reason" in validation && validation.reason === "name-too-long"
      ? "locationGroups.errors.nameTooLong"
      : null;

  // A key thrown by `saveGroup` always wins: it describes a rule the write
  // actually refused, which outranks anything derived from the local state.
  const nameErrorKey =
    (errorKey && NAME_ERROR_KEYS.has(errorKey) && errorKey) ||
    (nameBlurred && localNameErrorKey) ||
    null;

  const areasErrorKey =
    (errorKey && AREAS_ERROR_KEYS.has(errorKey) && errorKey) ||
    (areasEmptied && !locations.length && "locationGroups.errors.noLocations") ||
    null;

  const generalErrorKey =
    errorKey && !NAME_ERROR_KEYS.has(errorKey) && !AREAS_ERROR_KEYS.has(errorKey) ? errorKey : null;

  const atCap = locations.length >= MAX_LOCATIONS_PER_LOCATION_GROUP;

  const handleLocationsChange = (next: ProcessedListingAddress[]): void => {
    // `LocationPicker` echoes its state back on mount too, so this only reads as
    // "emptied" on a genuine non-empty → empty transition.
    if (locations.length && !next.length) setAreasEmptied(true);
    if (next.length) setAreasEmptied(false);
    setLocations(next);
  };

  const handleSave = async (): Promise<void> => {
    setErrorKey(null);
    if (!isValid) return;

    setSaving(true);
    try {
      const saved = await saveGroup({
        ...(group?.id ? { id: group.id } : {}),
        name: trimmedName,
        ...(emoji ? { emoji } : {}),
        locations
      });
      onSaved(saved);
    } catch (error) {
      setErrorKey((error as Error)?.message || "locationGroups.errors.saveFailed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <TextField
        fullWidth
        autoFocus
        size="small"
        value={name}
        label={t("locationGroups.nameLabel")}
        placeholder={t("locationGroups.namePlaceholder") as string}
        error={Boolean(nameErrorKey)}
        helperText={nameErrorKey ? (t(nameErrorKey) as string) : " "}
        // The cap is enforced by the input as well as by the validator, so the
        // error is a fallback for a paste rather than the normal way to meet it.
        inputProps={{ maxLength: MAX_LOCATION_GROUP_NAME_LENGTH }}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => setNameBlurred(true)}
        /* Enter in a single-line name field means "save" to everyone who has ever
           filled in a form. There is no <form> here to do it, so it is wired by
           hand — and `preventDefault` keeps the key from being read as anything
           else on the way out. When the form is not yet valid it deliberately does
           nothing but mark the field touched, which is what surfaces the reason. */
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          setNameBlurred(true);
          if (!saving && isValid) void handleSave();
        }}
      />

      <Box>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
        >
          {t("locationGroups.emojiLabel")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={emoji}
          sx={{
            mt: 0.75,
            flexWrap: "wrap",
            gap: 0.5,
            // The group's own rules collapse its children into one pill with
            // negative margins and shared corners; this is a ROW of independent
            // badges, so each keeps its own border and radius.
            "& .MuiToggleButtonGroup-grouped": {
              m: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "10px !important"
            }
          }}
          // `null` is what MUI sends when the active button is tapped again —
          // treat it as "no badge" rather than ignoring it, so the toggle can be
          // switched off the same way it was switched on.
          onChange={(_event, next) => setEmoji(next ?? "")}
        >
          {EMOJI_CHOICES.map((choice) => (
            <ToggleButton key={choice} value={choice} sx={{ width: 38, height: 38, fontSize: 18 }}>
              {choice}
            </ToggleButton>
          ))}
          {/* "no badge" — an icon rather than a word, because there is no string
              here to translate and a crossed circle reads the same in both
              languages. Tapping the active emoji again does the same thing (MUI
              sends `null`); this is the affordance that says so. */}
          <ToggleButton value="" sx={{ width: 38, height: 38 }}>
            <Block sx={{ width: 18, height: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
          >
            {t("locationGroups.areasLabel")}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: atCap ? "warning.dark" : "text.secondary" }}
          >
            {t("locationGroups.areasCount", {
              count: locations.length,
              max: MAX_LOCATIONS_PER_LOCATION_GROUP
            })}
          </Typography>
        </Stack>

        <LocationPicker
          small
          hideQuickTabs
          maxPicks={MAX_LOCATIONS_PER_LOCATION_GROUP}
          pickedLocations={locations}
          onLocationsChange={handleLocationsChange}
        />

        {(!locations.length || areasErrorKey) && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: areasErrorKey ? "error.main" : "text.secondary" }}
          >
            {areasErrorKey ? t(areasErrorKey) : t("locationGroups.areasEmpty")}
          </Typography>
        )}
      </Box>

      {generalErrorKey && (
        <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700 }}>
          {t(generalErrorKey, { defaultValue: generalErrorKey })}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="text"
          color="inherit"
          disabled={saving}
          onClick={onCancel}
          sx={{ color: "text.secondary", fontWeight: 700 }}
        >
          {t("locationGroups.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!isValid}
          onClick={handleSave}
          sx={{ fontWeight: 800, borderRadius: "12px", px: 3 }}
        >
          {t("locationGroups.save")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
